import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Spin, Tag, Tooltip, Typography } from 'antd';

import type {
  InspectAssessmentPeriod,
  InspectAssessmentPeriodPrices,
  InspectAssessmentSliderDirection,
  InspectAssessmentSliderDirectionInstrument,
  InspectAssessmentSliderDirectionPeriod,
} from '../../../../state/app/inspect_assessment/types';
import { inspectAssessmentPeriodPriceKey } from '../../../../state/app/inspect_assessment/actions';
import { assessmentPeriodMarketChanges } from './chartData';
import PeriodChangeTag from './PeriodChangeTag';

export type PillarOneSliderWorkspaceProps = {
  active: boolean;
  assessmentId?: number;
  audit?: InspectAssessmentSliderDirection;
  error?: string;
  fetched: boolean;
  fetching: boolean;
  hasSelectedAttempt: boolean;
  onLoad: () => void;
  periodPricesByKey: Record<string, InspectAssessmentPeriodPrices>;
  periods: InspectAssessmentPeriod[];
};

export type PillarOneSliderScoreMode = 'direction' | 'magnitude' | 'restraint';

type PeriodAudit = {
  period: InspectAssessmentPeriod;
  score?: InspectAssessmentSliderDirectionPeriod;
};

const RAW_PCS_HELP =
  "For each instrument and period, raw PCS is the signed sum of that instrument's ICS impact values across all news in the period.";

const RETURN_HELP =
  "The instrument's return from the start of this period to the start of the next period.";

const DIRECTION_SCORE_HELP =
  'Same direction earns 100%, a zero slider earns 45%, and the opposite direction earns 0%. Adapted PCS below 0.35 in absolute value is excluded; the rest are weighted by absolute adapted PCS.';

const DISTANCE_HELP =
  "The absolute gap, in slider points, between the user's signed slider value and the adapted PCS.";

const MAGNITUDE_SCORE_HELP =
  'An exact match earns 100%. Each slider point of distance removes 20 percentage points, reaching 0% at five or more points. Period scoring weights this credit by absolute adapted PCS.';

const AGREEMENT_HELP =
  'The assessment answer-key Agreement value measures how consistently the news impacts point in one direction. Below 0.35 is treated as unclear. A missing value is scored as 1.00.';

const RESTRAINT_REASON_HELP =
  'An instrument is assessed when adapted PCS is below 0.35 in absolute value, Agreement is below 0.35, or both. Clear instruments are shown but excluded.';

const DISTANCE_FROM_NEUTRAL_HELP =
  "The absolute size of the user's slider value. Zero is fully restrained; larger values receive less credit.";

const RESTRAINT_SCORE_HELP =
  'A zero slider earns 100%. Each point away from zero removes 25 percentage points, reaching 0% at four or five. Included instruments are averaged equally.';

const RESTRAINT_REASON_LABELS: Record<
  InspectAssessmentSliderDirectionInstrument['restraint_reason'],
  string
> = {
  small_pcs: 'Small PCS',
  low_agreement: 'Low agreement',
  both: 'Both',
  clear: 'Clear · excluded',
};

const ColumnHeading: React.FC<{
  help: string;
  label: string;
}> = ({ help, label }) => (
  <span className="assessment-inspect-assessment__slider-direction-column-heading">
    <span>{label}</span>
    <Tooltip placement="topLeft" title={help}>
      <span
        aria-label={`About ${label}`}
        className="assessment-inspect-assessment__slider-direction-column-help"
        role="img"
        tabIndex={0}
      >
        <InfoCircleOutlined />
      </span>
    </Tooltip>
  </span>
);

const formatPoints = (points: number | null | undefined) =>
  typeof points === 'number' && Number.isFinite(points)
    ? points.toFixed(1)
    : '—';

const formatSigned = (value: number | null | undefined, decimals = 2) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${value > 0 ? '+' : ''}${value.toFixed(decimals)}`
    : '—';

const storedPointsForMode = (
  period: InspectAssessmentSliderDirectionPeriod | undefined,
  mode: PillarOneSliderScoreMode,
) => {
  if (mode === 'magnitude') return period?.stored_magnitude_points;
  if (mode === 'restraint') return period?.stored_restraint_points;
  return period?.stored_direction_points;
};

const totalWeightForMode = (
  period: InspectAssessmentSliderDirectionPeriod,
  mode: PillarOneSliderScoreMode,
) => {
  if (mode === 'magnitude') return period.magnitude_total_weight;
  if (mode === 'restraint') return period.restraint_instrument_count;
  return period.total_weight;
};

const reconcilesForMode = (
  period: InspectAssessmentSliderDirectionPeriod,
  mode: PillarOneSliderScoreMode,
) => {
  if (mode === 'magnitude') return period.magnitude_reconciles;
  if (mode === 'restraint') return period.restraint_reconciles;
  return period.reconciles;
};

const SliderInstrumentRow = React.memo<{
  instrument: InspectAssessmentSliderDirectionInstrument;
  mode: PillarOneSliderScoreMode;
  prices?: InspectAssessmentPeriodPrices;
}>(({ instrument, mode, prices }) => {
  const periodReturn = React.useMemo(
    () => assessmentPeriodMarketChanges(prices).pricePercentage,
    [prices],
  );
  const directionCreditPercent =
    instrument.direction_credit === null
      ? undefined
      : Math.round(instrument.direction_credit * 100);
  const directionCreditColor =
    directionCreditPercent === 100
      ? 'green'
      : directionCreditPercent === 45
        ? 'gold'
        : 'red';
  const magnitudeCreditPercent = Math.round(instrument.magnitude_credit * 100);
  const magnitudeCreditColor =
    magnitudeCreditPercent === 100
      ? 'green'
      : magnitudeCreditPercent >= 60
        ? 'blue'
        : magnitudeCreditPercent > 0
          ? 'gold'
          : 'red';
  const restraintCreditPercent =
    instrument.restraint_credit === null
      ? undefined
      : Math.round(instrument.restraint_credit * 100);
  const restraintCreditColor =
    restraintCreditPercent === 100
      ? 'green'
      : restraintCreditPercent === 75
        ? 'blue'
        : restraintCreditPercent && restraintCreditPercent > 0
          ? 'gold'
          : 'red';
  const restraintReasonColor =
    instrument.restraint_reason === 'both'
      ? 'purple'
      : instrument.restraint_reason === 'low_agreement'
        ? 'gold'
        : instrument.restraint_reason === 'small_pcs'
          ? 'blue'
          : undefined;

  return (
    <tr>
      <th scope="row">
        {mode === 'restraint' ? (
          <Tooltip placement="topLeft" title={instrument.instrument_code}>
            <span className="assessment-inspect-assessment__slider-restraint-instrument">
              {instrument.instrument_code}
            </span>
          </Tooltip>
        ) : (
          instrument.instrument_code
        )}
      </th>
      {mode !== 'restraint' && <td>{formatSigned(instrument.raw_pcs)}</td>}
      <td>
        <PeriodChangeTag
          change={{
            key: 'return',
            label: 'Return',
            unit: '%',
            value: periodReturn,
          }}
        />
      </td>
      <td>{formatSigned(instrument.adapted_pcs)}</td>
      <td>
        <span>{formatSigned(instrument.slider_value, 0)}</span>
        {!instrument.slider_was_set && (
          <small>
            {mode === 'restraint' ? 'Not set · scored as 0' : 'Not set'}
          </small>
        )}
      </td>
      {mode === 'restraint' ? (
        <>
          <td>
            {instrument.agreement.toFixed(2)}
            {instrument.raw_agreement === null && (
              <small>Defaulted to 1.00</small>
            )}
          </td>
          <td>
            <Tag color={restraintReasonColor} variant="filled">
              {RESTRAINT_REASON_LABELS[instrument.restraint_reason]}
            </Tag>
          </td>
          <td>{instrument.distance_from_neutral.toFixed(0)}</td>
          <td>
            {restraintCreditPercent === undefined ? (
              <Tag variant="filled">Excluded</Tag>
            ) : (
              <Tag color={restraintCreditColor} variant="filled">
                {restraintCreditPercent}%
              </Tag>
            )}
            <small>
              {instrument.restraint_point_contribution === null
                ? 'Not averaged'
                : `${instrument.restraint_point_contribution.toFixed(2)} pts · equal weight`}
            </small>
          </td>
        </>
      ) : mode === 'magnitude' ? (
        <>
          <td>{instrument.distance.toFixed(2)}</td>
          <td>
            <Tag color={magnitudeCreditColor} variant="filled">
              {magnitudeCreditPercent}%
            </Tag>
            <small>
              {instrument.magnitude_point_contribution === null
                ? 'No magnitude weight'
                : `${instrument.magnitude_point_contribution.toFixed(2)} pts · weight ${instrument.magnitude_weight.toFixed(2)}`}
            </small>
          </td>
        </>
      ) : (
        <td>
          {directionCreditPercent === undefined ? (
            <Tag variant="filled">Neutral · excluded</Tag>
          ) : (
            <Tag color={directionCreditColor} variant="filled">
              {directionCreditPercent}%
            </Tag>
          )}
          <small>
            {instrument.point_contribution === null
              ? 'No direction weight'
              : `${instrument.point_contribution.toFixed(2)} pts · weight ${instrument.weight.toFixed(2)}`}
          </small>
        </td>
      )}
    </tr>
  );
});

SliderInstrumentRow.displayName = 'SliderInstrumentRow';

export const PillarOneSliderScoreWorkspace: React.FC<
  PillarOneSliderWorkspaceProps & { mode: PillarOneSliderScoreMode }
> = ({
  active,
  assessmentId,
  audit,
  error,
  fetched,
  fetching,
  hasSelectedAttempt,
  onLoad,
  periodPricesByKey,
  periods,
  mode,
}) => {
  const detailsRef = React.useRef<HTMLDivElement>(null);
  const sectionRefs = React.useRef(new Map<number, HTMLElement>());
  const scrollFrameRef = React.useRef<number>();
  const [activePeriodIndex, setActivePeriodIndex] = React.useState<
    number | undefined
  >(periods[0]?.index);

  React.useEffect(() => {
    if (
      active &&
      hasSelectedAttempt &&
      !audit &&
      !fetching &&
      !fetched &&
      !error
    ) {
      onLoad();
    }
  }, [active, audit, error, fetched, fetching, hasSelectedAttempt, onLoad]);

  const periodAudits = React.useMemo<PeriodAudit[]>(() => {
    if (!active) return [];
    const scoresByPeriod = new Map(
      (audit?.periods ?? []).map(period => [period.period_index, period]),
    );
    return periods.map(period => ({
      period,
      score: scoresByPeriod.get(period.index),
    }));
  }, [active, audit, periods]);

  React.useEffect(() => {
    if (!active) return;
    if (
      activePeriodIndex === undefined ||
      !periodAudits.some(item => item.period.index === activePeriodIndex)
    ) {
      setActivePeriodIndex(periodAudits[0]?.period.index);
    }
  }, [active, activePeriodIndex, periodAudits]);

  React.useEffect(
    () => () => {
      if (scrollFrameRef.current !== undefined) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    [],
  );

  const scrollToPeriod = (periodIndex: number) => {
    const details = detailsRef.current;
    const section = sectionRefs.current.get(periodIndex);
    if (!details || !section) return;
    setActivePeriodIndex(periodIndex);
    details.scrollTo({
      behavior: 'smooth',
      top: Math.max(0, section.offsetTop - 6),
    });
  };

  const syncActivePeriod = () => {
    if (scrollFrameRef.current !== undefined) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      const details = detailsRef.current;
      if (!details) return;
      const threshold = details.scrollTop + 16;
      let nextPeriodIndex = periodAudits[0]?.period.index;
      for (const item of periodAudits) {
        const section = sectionRefs.current.get(item.period.index);
        if (!section || section.offsetTop > threshold) break;
        nextPeriodIndex = item.period.index;
      }
      setActivePeriodIndex(nextPeriodIndex);
    });
  };

  if (!active) return null;

  const isMagnitude = mode === 'magnitude';
  const isRestraint = mode === 'restraint';
  const scoreName = isRestraint
    ? 'restraint when unclear'
    : isMagnitude
      ? 'magnitude'
      : 'direction';
  const scoreTitle = isRestraint
    ? 'Restraint'
    : isMagnitude
      ? 'Magnitude'
      : 'Direction';
  const scoreSubject = isRestraint ? scoreName : 'slider ' + scoreName;
  const maximumPoints = isRestraint ? 10 : 25;

  if (!hasSelectedAttempt) {
    return (
      <div className="assessment-inspect-assessment__pillar-one-status">
        <Typography.Text type="secondary">
          {`Select a user with an assessment session to inspect ${scoreSubject}.`}
        </Typography.Text>
      </div>
    );
  }

  const reconciliationFailures =
    audit?.periods.filter(
      period => reconcilesForMode(period, mode) === false,
    ) ?? [];
  const adaptedPcsHelp =
    audit?.calibration.kind === 'tanh' && audit.calibration.sigma !== null
      ? `Raw PCS is mapped onto the -5 to +5 slider scale using 5 × tanh(raw PCS / ${audit.calibration.sigma}), while preserving its direction.`
      : 'Raw PCS is already on the -5 to +5 slider scale, so it is used unchanged.';
  const periodIndexLabel = isRestraint
    ? 'Restraint when unclear period index'
    : 'Slider ' + scoreName + ' period index';

  return (
    <div className="assessment-inspect-assessment__pillar-one-audit">
      <nav
        aria-label={periodIndexLabel}
        className="assessment-inspect-assessment__pillar-one-period-index"
      >
        {periodAudits.map(item => (
          <button
            aria-current={
              activePeriodIndex === item.period.index ? 'location' : undefined
            }
            className={`assessment-inspect-assessment__pillar-one-period-link ${
              activePeriodIndex === item.period.index
                ? 'assessment-inspect-assessment__pillar-one-period-link--active'
                : ''
            }`}
            key={item.period.index}
            onClick={() => scrollToPeriod(item.period.index)}
            onFocus={() => scrollToPeriod(item.period.index)}
            onMouseEnter={() => scrollToPeriod(item.period.index)}
            type="button"
          >
            <span>{item.period.label}</span>
            <span className="assessment-inspect-assessment__pillar-one-period-score">
              <span>
                {formatPoints(storedPointsForMode(item.score, mode))}
                <small>/{maximumPoints}</small>
              </span>
            </span>
          </button>
        ))}
      </nav>

      <div
        className="assessment-inspect-assessment__pillar-one-period-details"
        onScroll={syncActivePeriod}
        ref={detailsRef}
      >
        {fetching && !audit && (
          <div className="assessment-inspect-assessment__pillar-one-period-status">
            <Spin size="small" />
            <Typography.Text type="secondary">
              Loading {scoreSubject} details…
            </Typography.Text>
          </div>
        )}
        {error && (
          <Alert
            action={
              <Button onClick={onLoad} size="small">
                Retry
              </Button>
            }
            description={error}
            title={`Unable to load ${scoreSubject} details`}
            showIcon
            type="error"
          />
        )}
        {audit?.result_id === null && (
          <Alert
            description="This assessment attempt has no saved result. No score fallback was applied."
            title={`${scoreTitle} score unavailable`}
            showIcon
            type="warning"
          />
        )}
        {audit?.result_id !== null && audit?.periods.length === 0 && (
          <Alert
            description="This result has no saved Pillar 1 period scores. No score fallback was applied."
            title={`${scoreTitle} score unavailable`}
            showIcon
            type="warning"
          />
        )}
        {reconciliationFailures.length > 0 && (
          <Alert
            description={`${reconciliationFailures.length} period${
              reconciliationFailures.length === 1 ? '' : 's'
            } no longer reproduce the saved score from the available PCS${
              isRestraint ? ', Agreement,' : ''
            } and slider evidence. Saved sidecar scores remain authoritative.`}
            title={`${scoreTitle} details do not fully reconcile`}
            showIcon
            type="warning"
          />
        )}

        {periodAudits.map(item => (
          <section
            className="assessment-inspect-assessment__pillar-one-period-section"
            key={item.period.index}
            ref={node => {
              if (node) sectionRefs.current.set(item.period.index, node);
              else sectionRefs.current.delete(item.period.index);
            }}
          >
            <header className="assessment-inspect-assessment__pillar-one-period-header">
              <Typography.Text strong>{item.period.label}</Typography.Text>
              <span className="assessment-inspect-assessment__pillar-one-period-metrics">
                <Tag color="blue" variant="filled">
                  {scoreTitle} points{' '}
                  {formatPoints(storedPointsForMode(item.score, mode))}/
                  {maximumPoints}
                </Tag>
                {isRestraint && item.score && (
                  <Tag variant="filled">
                    {item.score.restraint_instrument_count} /{' '}
                    {item.score.instruments.length} assessed
                  </Tag>
                )}
              </span>
            </header>

            {fetching && !audit ? (
              <div className="assessment-inspect-assessment__pillar-one-period-status">
                <Spin size="small" />
              </div>
            ) : !audit ? (
              <Typography.Text type="secondary">
                {scoreTitle} details are unavailable for this period.
              </Typography.Text>
            ) : !item.score ? (
              <Typography.Text type="secondary">
                No saved {scoreName} score is available for this period.
              </Typography.Text>
            ) : item.score.instruments.length === 0 ? (
              <Typography.Text type="secondary">
                No scored conviction instruments are available for this period.
              </Typography.Text>
            ) : (
              <>
                {totalWeightForMode(item.score, mode) <= 0 && (
                  <Typography.Text
                    className="assessment-inspect-assessment__slider-direction-default"
                    type="secondary"
                  >
                    {isRestraint
                      ? 'No instruments met either unclear-view check; the scoring rule defaults this component to 10/10.'
                      : isMagnitude
                        ? 'All adapted PCS values have zero magnitude weight; the scoring rule defaults this component to 25/25.'
                        : 'No adapted PCS was active for direction comparison; the scoring rule defaults this component to 25/25.'}
                  </Typography.Text>
                )}
                {isRestraint &&
                  item.score.restraint_zero_average_fallback_applied && (
                    <Alert
                      className="assessment-inspect-assessment__pillar-one-period-warning"
                      description="Every assessed instrument earned 0% restraint credit, but the current scoring fallback converted the exact zero average to 10/10. The saved sidecar score remains authoritative."
                      title="Current full-credit fallback applied"
                      showIcon
                      type="warning"
                    />
                  )}
                <div className="assessment-inspect-assessment__slider-direction-table-wrap">
                  <table
                    className={`assessment-inspect-assessment__slider-direction-table ${
                      isRestraint
                        ? 'assessment-inspect-assessment__slider-direction-table--restraint'
                        : isMagnitude
                          ? 'assessment-inspect-assessment__slider-direction-table--magnitude'
                          : ''
                    }`}
                  >
                    <thead>
                      <tr>
                        <th scope="col">Instrument</th>
                        {!isRestraint && (
                          <th scope="col">
                            <ColumnHeading
                              help={RAW_PCS_HELP}
                              label="Raw PCS"
                            />
                          </th>
                        )}
                        <th scope="col">
                          <ColumnHeading help={RETURN_HELP} label="Return" />
                        </th>
                        <th scope="col">
                          <ColumnHeading
                            help={adaptedPcsHelp}
                            label="Adapted PCS"
                          />
                        </th>
                        <th scope="col">
                          User slider (
                          {
                            item.score.instruments.filter(
                              instrument => instrument.slider_was_set,
                            ).length
                          }{' '}
                          / {item.score.instruments.length})
                        </th>
                        {isRestraint ? (
                          <>
                            <th scope="col">
                              <ColumnHeading
                                help={AGREEMENT_HELP}
                                label="Agreement"
                              />
                            </th>
                            <th scope="col">
                              <ColumnHeading
                                help={RESTRAINT_REASON_HELP}
                                label="Why assessed"
                              />
                            </th>
                            <th scope="col">
                              <ColumnHeading
                                help={DISTANCE_FROM_NEUTRAL_HELP}
                                label="Distance from neutral"
                              />
                            </th>
                            <th scope="col">
                              <ColumnHeading
                                help={RESTRAINT_SCORE_HELP}
                                label="Restraint score"
                              />
                            </th>
                          </>
                        ) : isMagnitude ? (
                          <>
                            <th scope="col">
                              <ColumnHeading
                                help={DISTANCE_HELP}
                                label="Distance"
                              />
                            </th>
                            <th scope="col">
                              <ColumnHeading
                                help={MAGNITUDE_SCORE_HELP}
                                label="Magnitude score"
                              />
                            </th>
                          </>
                        ) : (
                          <th scope="col">
                            <ColumnHeading
                              help={DIRECTION_SCORE_HELP}
                              label="Direction score"
                            />
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {item.score.instruments.map(instrument => {
                        const prices =
                          assessmentId === undefined
                            ? undefined
                            : periodPricesByKey[
                                inspectAssessmentPeriodPriceKey(
                                  assessmentId,
                                  item.period.index,
                                  instrument.instrument_id,
                                )
                              ];
                        return (
                          <SliderInstrumentRow
                            instrument={instrument}
                            key={instrument.instrument_id}
                            mode={mode}
                            prices={prices}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

const PillarOneSliderDirectionWorkspace: React.FC<
  PillarOneSliderWorkspaceProps
> = props => <PillarOneSliderScoreWorkspace {...props} mode="direction" />;

export default PillarOneSliderDirectionWorkspace;
